import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Lock, Mail, User, Eye, EyeOff } from 'lucide-react';
import PublicNav from './components/PublicNav';

const API_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3001');

export default function CreateAccount() {
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = location.state?.returnTo;
  const returnQuery = location.state?.query;
  // Accept a pre-selected account type from navigation state so the
  // landing page can deep-link new gardeners straight into the gardener
  // signup flow without them having to pick from the toggle.
  const initialAccountType =
    location.state?.accountType === 'designer' ? 'designer' : 'customer';
  // Invite flows (e.g. garden handoff) pass a locked email so the new account
  // matches the invited address. Designer flow never locks the email.
  const lockedEmail = (location.state?.lockedEmail || '').toString().trim();
  const inviteMessage = location.state?.inviteMessage || null;
  const [showPassword, setShowPassword] = useState(false);
  const [accountType, setAccountType] = useState(initialAccountType);
  const [formData, setFormData] = useState({
    email: lockedEmail || '',
    password: '',
    fullName: '',
    phone: '',
    city: '',
    country: '',
    provinceState: '',
    agreeToTerms: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);
    setLoading(true);

    // Validation
    if (!formData.fullName || !formData.email || !formData.password || 
        !formData.phone || !formData.city || !formData.country || !formData.agreeToTerms) {
      setError('Please fill in all required fields and agree to terms');
      setLoading(false);
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
          fullName: formData.fullName,
          phone: formData.phone,
          city: formData.city,
          country: formData.country,
          provinceState: formData.provinceState,
          role: accountType, // 'customer' or 'designer'
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Registration failed. Please try again.');
        setLoading(false);
        return;
      }

      // If token is returned, user is automatically logged in
      if (data.token && data.user) {
        localStorage.setItem('authToken', data.token);
        localStorage.setItem('userData', JSON.stringify(data.user));
        // Also store as 'user' for compatibility with PlantADayApp
        localStorage.setItem('user', JSON.stringify(data.user));
        const message = accountType === 'designer'
          ? 'Designer account created successfully! You are now logged in. Complete your profile to appear in "Find a Designer".'
          : 'Welcome to PlantaDay! Taking you to your garden...';
        setSuccessMessage(message);
        setTimeout(() => {
          if (returnTo) {
            if (returnQuery) {
              navigate(returnTo, { state: { query: returnQuery } });
            } else {
              navigate(returnTo);
            }
          } else if (accountType === 'customer') {
            // New gardeners go straight to the garden screen. The old
            // /yard-profile destination was a legacy designer-flow detour.
            window.location.href = '/garden';
          } else {
            window.location.href = '/';
          }
        }, 2000);
      } else {
        const message = accountType === 'designer'
          ? 'Designer account created successfully! Redirecting to login...'
          : 'Account created successfully! Redirecting to login...';
        setSuccessMessage(message);
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      }
    } catch (err) {
      console.error('Registration error:', err);
      setError('Unable to connect to server. Please try again.');
      setLoading(false);
    }
  }

  const currentUserId = (() => {
    try {
      const raw = localStorage.getItem('userData') || localStorage.getItem('user');
      if (!raw) return null;
      return JSON.parse(raw)?.id || null;
    } catch {
      return null;
    }
  })();

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 flex flex-col">
      <PublicNav currentUserId={currentUserId} />
      <div className="flex-1 flex items-center justify-center py-8 sm:py-12 px-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-6 sm:p-8">
        <div className="text-center mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
            {accountType === 'designer' ? 'Create designer account' : 'Start your garden'}
          </h1>
          <p className="text-sm sm:text-base text-gray-600 mb-4">
            {accountType === 'designer'
              ? 'Get access to the designer dashboard, projects, and the Find a Designer directory.'
              : 'Track your zones, plants, and seasonal tasks. Free to use.'}
          </p>

          {/* Account type selection. Gardener is the default and gets the
              visual emphasis; designer is a secondary option. */}
          <div className="grid grid-cols-2 gap-2 sm:gap-3 mb-6">
            <button
              type="button"
              onClick={() => setAccountType('customer')}
              aria-pressed={accountType === 'customer'}
              className={`p-3 sm:p-4 rounded-lg border-2 transition-all min-h-[44px] text-left ${
                accountType === 'customer'
                  ? 'border-green-600 bg-green-50 shadow-sm'
                  : 'border-gray-200 hover:border-gray-300 opacity-90'
              }`}
            >
              <div className="text-xl sm:text-2xl mb-1 sm:mb-2">🌿</div>
              <div className="font-semibold text-sm sm:text-base text-gray-900">Gardener</div>
              <div className="text-xs text-gray-600 mt-1">
                Track your plants, zones, and seasonal tasks.
              </div>
            </button>
            <button
              type="button"
              onClick={() => setAccountType('designer')}
              aria-pressed={accountType === 'designer'}
              className={`p-3 sm:p-4 rounded-lg border-2 transition-all min-h-[44px] text-left ${
                accountType === 'designer'
                  ? 'border-green-600 bg-green-50 shadow-sm'
                  : 'border-gray-200 hover:border-gray-300 opacity-90'
              }`}
            >
              <div className="text-xl sm:text-2xl mb-1 sm:mb-2">📐</div>
              <div className="font-semibold text-sm sm:text-base text-gray-900">Designer</div>
              <div className="text-xs text-gray-600 mt-1">
                Dashboard, projects, client directory.
              </div>
            </button>
          </div>
        </div>

        {inviteMessage && (
          <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 p-3 rounded-lg text-sm mb-4">
            {inviteMessage}
          </div>
        )}

        {error && (
          <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm mb-4">
            {error}
          </div>
        )}

        {successMessage && (
          <div className="bg-green-50 text-green-700 p-3 rounded-lg text-sm mb-4">
            {successMessage}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Full Name *
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                value={formData.fullName}
                onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                className="w-full pl-10 pr-4 py-3 sm:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-base sm:text-sm min-h-[44px]"
                placeholder="John Doe"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email *
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className={`w-full pl-10 pr-4 py-3 sm:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-base sm:text-sm min-h-[44px] ${lockedEmail ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                placeholder="john@example.com"
                required
                readOnly={!!lockedEmail}
                aria-readonly={!!lockedEmail}
                title={lockedEmail ? 'This invite was sent to this email address.' : undefined}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Password *
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="At least 8 characters"
                required
                minLength={8}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone *
            </label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              placeholder="+1 (555) 123-4567"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                City *
              </label>
              <input
                type="text"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="City"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Province/State
              </label>
              <input
                type="text"
                value={formData.provinceState}
                onChange={(e) => setFormData({ ...formData, provinceState: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Province/State"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Country *
            </label>
            <input
              type="text"
              value={formData.country}
              onChange={(e) => setFormData({ ...formData, country: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              placeholder="Country"
              required
            />
          </div>

          <div className="flex items-start">
            <input
              type="checkbox"
              id="agreeToTerms"
              checked={formData.agreeToTerms}
              onChange={(e) => setFormData({ ...formData, agreeToTerms: e.target.checked })}
              className="mt-1 mr-2"
              required
            />
            <label htmlFor="agreeToTerms" className="text-sm text-gray-700">
              I agree to the{' '}
              <Link to="/terms" className="text-green-600 hover:text-green-700 underline">
                Terms of Service
              </Link>{' '}
              and{' '}
              <Link to="/privacy" className="text-green-600 hover:text-green-700 underline">
                Privacy Policy
              </Link>
            </label>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-3 px-4 rounded-lg hover:from-emerald-700 hover:to-teal-700 font-semibold shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] text-sm sm:text-base"
          >
            {loading ? 'Creating Account...' : 'Create Account'}
          </button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600">
            Already have an account?{' '}
            <Link to="/login" className="text-green-600 hover:text-green-700 font-medium">
              Sign In
            </Link>
          </p>
        </div>
      </div>
      </div>
    </div>
  );
}

